import { Link } from 'react-router';
import { ArrowRight, CalendarClock, CheckCircle2, ClipboardCheck, Sparkles, Users } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { WarmMarketingBackdrop } from '../components/marketing/MarketingVisuals';
import { EnterpriseProofPack } from '../components/enterprise/EnterpriseProofPack';
import { EnterpriseSalesNav } from '../components/enterprise/EnterpriseSalesNav';
import { useSEO } from '../lib/seo';

const phases = [
  {
    title: 'Discovery and scope',
    timeline: 'Week 1',
    details: 'Define target cohorts, business outcomes, privacy boundaries, and decision owners across HR, benefits, and operations.',
  },
  {
    title: 'Provisioning and launch',
    timeline: 'Weeks 2-3',
    details: 'Provision enterprise workspace, configure role-scoped controls, and launch participant onboarding with support playbooks.',
  },
  {
    title: 'Measurement and handoff',
    timeline: 'Weeks 4-8',
    details: 'Review anonymized trend movement, intervention impact, and rollout economics for expansion approval.',
  },
];

export default function EnterprisePilot() {
  useSEO({
    title: 'Enterprise Pilot Program | TENSION for Work',
    description:
      'Plan a pilot for TENSION Enterprise with scoped cohorts, anonymized insights, and a measurable rollout framework.',
    canonical: '/enterprise/pilot',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    keywords: ['enterprise pilot framework', 'relationship wellness pilot', 'workplace wellbeing pilot'],
  });

  return (
    <WarmMarketingBackdrop>
      <EnterpriseSalesNav variant="light" />
      <div className="enterprise-public-shell mx-auto min-h-screen max-w-5xl space-y-6 px-4 py-10 md:py-14">
        <header className="enterprise-hero-shell">
          <div className="enterprise-hero-overlay" />
          <div className="enterprise-hero-content">
          <img
            src="/branding/tension-enterprise-logo.svg"
            alt="TENSION Enterprise"
            className="mb-4 h-auto w-full max-w-md rounded-xl border border-slate-700/70 bg-slate-950/70 p-1"
          />
          <p className="inline-flex rounded-full bg-violet-100 px-3 py-1 text-xs font-bold uppercase tracking-[0.14em] text-violet-700">
            Pilot framework
          </p>
          <h1 className="mt-3 text-3xl font-black tracking-tight text-slate-900 md:text-5xl">Pilot model designed for fast enterprise decisions</h1>
          <p className="mt-3 max-w-3xl text-slate-700 md:text-lg">
            The pilot framework is engineered to produce executive-grade evidence quickly: adoption, risk movement, and rollout readiness.
          </p>
          </div>
        </header>

        <div className="grid gap-4 md:grid-cols-3">
          {phases.map((phase) => (
            <Card key={phase.title} className="enterprise-surface-card enterprise-lift-card p-6">
              <div className="enterprise-icon-chip">
                <CalendarClock className="h-7 w-7 text-violet-600" aria-hidden />
              </div>
              <p className="mt-3 text-xs font-bold uppercase tracking-wide text-violet-700">{phase.timeline}</p>
              <h2 className="mt-1 text-lg font-semibold text-slate-900">{phase.title}</h2>
              <p className="mt-2 text-sm text-slate-700">{phase.details}</p>
            </Card>
          ))}
        </div>

        <section className="enterprise-surface-card rounded-2xl border-fuchsia-100 p-6 md:p-8">
          <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900 md:text-2xl">
            <Sparkles className="h-5 w-5 text-fuchsia-600" aria-hidden />
            Pilot success criteria examples
          </h2>
          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <div className="flex gap-2 text-sm text-slate-600">
              <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" aria-hidden />
              <span>Participant engagement and completion rates</span>
            </div>
            <div className="flex gap-2 text-sm text-slate-600">
              <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" aria-hidden />
              <span>Trend movement in anonymized tension-risk cohorts</span>
            </div>
            <div className="flex gap-2 text-sm text-slate-600">
              <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" aria-hidden />
              <span>Manager and benefits partner qualitative feedback</span>
            </div>
            <div className="flex gap-2 text-sm text-slate-600">
              <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" aria-hidden />
              <span>Readiness for expanded rollout and procurement stage</span>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/business-inquiries?inquiryType=enterprise">
              <Button className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white hover:opacity-95">
                Start pilot conversation <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/enterprise/solutions">
              <Button variant="outline" className="border-violet-200 bg-white text-violet-800">
                Review use-cases
              </Button>
            </Link>
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-3">
          <Card className="enterprise-surface-card enterprise-lift-card p-5 hover:shadow-lg">
            <Users className="h-6 w-6 text-violet-600" aria-hidden />
            <h3 className="mt-2 font-semibold text-slate-900">Cohort-first design</h3>
            <p className="mt-1 text-sm text-slate-700">Cohort-based design keeps reporting practical, anonymous, and actionable.</p>
          </Card>
          <Card className="enterprise-surface-card enterprise-lift-card p-5 hover:shadow-lg">
            <ClipboardCheck className="h-6 w-6 text-violet-600" aria-hidden />
            <h3 className="mt-2 font-semibold text-slate-900">Defined milestones</h3>
            <p className="mt-1 text-sm text-slate-700">Each phase has owners, milestones, and measurable outputs.</p>
          </Card>
          <Card className="enterprise-surface-card enterprise-lift-card p-5 hover:shadow-lg">
            <CalendarClock className="h-6 w-6 text-violet-600" aria-hidden />
            <h3 className="mt-2 font-semibold text-slate-900">Fast decision cycle</h3>
            <p className="mt-1 text-sm text-slate-700">Pilot results are packaged for clear go/no-go decisions and budget alignment.</p>
          </Card>
        </section>

        <EnterpriseProofPack compact />
      </div>
    </WarmMarketingBackdrop>
  );
}
